import { useCallback, useEffect, useMemo, useState } from 'react'
import * as api from '../lib/api'
import { useCanAct } from '../lib/access'
import Drawer from '../components/Drawer'
import Select from '../components/Select'
import { SkeletonRow } from '../components/Skeleton'
import Modal from '../components/Modal'
import { whenText } from '../lib/when'
import { CATEGORY_LABEL, HISTORY_LABEL, amountText, categoryLabel, durationText, isDone, workedMsNow } from '../lib/remediation'
import WorkPill from '../components/WorkPill'
import SampleTag from '../components/SampleTag'
import { isSampleId } from '../lib/samples'

/**
 * Ledger Remediation — the admin's view of every our-side ledger fix, across
 * every accountant. Read-only: items are raised and routed automatically (a
 * fixed category → accountant table, no manual assign, no queue), and each
 * accountant marks their own done on My Ledgers — nothing here writes.
 */

const STATUS_FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'confirmed', label: 'Done' },
  { value: 'all', label: 'All' },
]

const CATEGORY_FILTERS = [
  { value: 'all', label: 'All categories' },
  ...Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label })),
]

function Segmented({ options, value, onChange, title }) {
  return (
    <div className="seg" role="radiogroup" title={title}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={`seg-b${o.value === value ? ' on' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// Status last and narrow; the issue text gets whatever's left.
const ROW_COLS = 'minmax(130px,.9fr) 100px minmax(180px,1.6fr) 90px 130px 90px 120px'

function ItemRow({ item, accountantName, onOpen }) {
  const amount = amountText(item.amount)
  return (
    <div
      className="rowgrid rowbtn"
      style={{ gridTemplateColumns: ROW_COLS }}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
    >
      <span className="rm-code">
        {item.clientCode}
        <SampleTag show={isSampleId(item._id)} />
      </span>
      <span>
        <span className="rm-cat">{categoryLabel(item.category)}</span>
      </span>
      <span className="rm-desc-cell">{item.description}</span>
      <span className="rm-amt-cell">{amount ?? <span className="mute">—</span>}</span>
      <span className={item.assignedAccountantEmail ? 'rm-who' : 'rm-who unassigned'}>
        {item.assignedAccountantEmail ? accountantName(item.assignedAccountantEmail) : 'Unassigned'}
        {item.originalAccountantEmail && <span className="c-sub">covering for {accountantName(item.originalAccountantEmail)}</span>}
      </span>
      <span className="c-sub" style={{ marginTop: 0 }}>{whenText(item.createdAt)}</span>
      <span className="rm-status-cell">
        <WorkPill status={item.status} />
        {workedMsNow(item) > 0 && <span className="c-sub">{durationText(workedMsNow(item))} on it</span>}
      </span>
    </div>
  )
}

/** The detail drawer — the facts, the aggregate nudge the accountant is
 *  currently getting, and how it was closed. Nothing on it writes anything. */
function ItemDrawer({ itemId, accountantName, onClose }) {
  const [item, setItem] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    api
      .getRemediationItem(itemId)
      .then((out) => alive && setItem(out))
      .catch((e) => alive && setError(e.message))
    return () => {
      alive = false
    }
  }, [itemId])

  return (
    <Drawer
      title={item?.clientCode ?? 'Loading…'}
      subtitle={item ? categoryLabel(item.category) : null}
      summary={item ? <WorkPill status={item.status} /> : null}
      onClose={onClose}
    >
      {!item && !error && <div className="c-sub">Loading…</div>}
      {error && <div className="note">{error}</div>}
      {item && (
        <div className="rm-drawer">
          <div className="rm-quote">{item.description}</div>

          <dl className="kv">
            <dt>Amount</dt>
            <dd>{amountText(item.amount) ?? '—'}</dd>
            <dt>Reference</dt>
            <dd>{item.reference || '—'}</dd>
            <dt>Assigned to</dt>
            <dd>{item.assignedAccountantEmail ? accountantName(item.assignedAccountantEmail) : 'Unassigned'}</dd>
            <dt>Raised</dt>
            <dd>{whenText(item.createdAt)}</dd>
            {item.originalAccountantEmail && (
              <>
                <dt>Covering for</dt>
                <dd>{accountantName(item.originalAccountantEmail)} (away)</dd>
              </>
            )}
            <dt>Started</dt>
            <dd>{item.startedAt ? whenText(item.startedAt) : 'Not yet'}</dd>
            <dt>Time on it</dt>
            <dd>{workedMsNow(item) > 0 ? durationText(workedMsNow(item)) : '—'}</dd>
            {item.status === 'paused' && (
              <>
                <dt>Paused</dt>
                <dd>
                  {whenText(item.pausedAt)}
                  {item.pauseNote ? ` — ${item.pauseNote}` : ''}
                </dd>
              </>
            )}
            {item.channelPostedAt && (
              <>
                <dt>Posted</dt>
                <dd>{item.channelName ? `#${item.channelName}` : 'Cliq channel'}, {whenText(item.channelPostedAt)}</dd>
              </>
            )}
          </dl>

          {item.history?.length > 0 && (
            <div>
              <div className="rm-lbl" style={{ marginBottom: 8 }}>Timeline</div>
              <ol className="rm-timeline">
                {item.history.map((h, i) => (
                  <li key={i}>
                    <span className={`t-dot ${h.action}`} />
                    <span>
                      <b>{HISTORY_LABEL[h.action] ?? h.action}</b> {whenText(h.at)}
                      {h.by && <> by {accountantName(h.by)}</>}
                      {h.note && <> — “{h.note}”</>}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {item.status === 'confirmed' && (
            <div className="gate pass" style={{ margin: 0 }}>
              <span className="gi">✓</span>
              <span style={{ whiteSpace: 'normal' }}>
                Marked done by <b>{accountantName(item.confirmedBy)}</b> {whenText(item.confirmedAt)}
                {item.confirmedNote && <> — “{item.confirmedNote}”</>}
              </span>
            </div>
          )}

          {item.status !== 'confirmed' && item.nudgeMessagePreview && (
            <div>
              <div className="rm-lbl" style={{ marginBottom: 8 }}>Their current Cliq reminder</div>
              {/* Rendered the way a sent message looks in the Ledger
                  Collector's own thread — it's a fixed template, not
                  anything generated. Covers everything open for them, not
                  just this item. */}
              <div className="wa-row out">
                <div className="wa-bub out">
                  <span className="wa-text">{item.nudgeMessagePreview}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  )
}

/** Mark away / back — says exactly what will move before it moves. */
function AwayModal({ person, back, notStarted, onClose, onDone }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      const out = back ? await api.setAccountantBack(person.email) : await api.setAccountantAway(person.email)
      onDone(person, back, out)
    } catch (e) {
      setError(e.message)
      setBusy(false)
    }
  }
  return (
    <Modal
      title={back ? `Mark ${person.name} back?` : `Mark ${person.name} away?`}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : back ? 'Mark back' : 'Mark away'}
          </button>
        </>
      }
    >
      <div className="rm-confirm">
        {back ? (
          <p className="rm-confirm-lead">
            Anything of theirs the cover <b>hasn't opened yet</b> comes back to {person.name}. Fixes the cover has already
            started or finished stay with the cover. {person.name} gets a Cliq message saying what came back, and their
            daily reminder starts again.
          </p>
        ) : person.backupName ? (
          <p className="rm-confirm-lead">
            Their <b>{notStarted} not-started</b> fix{notStarted === 1 ? '' : 'es'} move to <b>{person.backupName}</b>, who
            gets a Cliq message about it. Fixes they've already started stay with them. New fixes for {person.name} go to{' '}
            {person.backupName} until they're marked back, and {person.name} gets no daily reminder while away.
          </p>
        ) : (
          <p className="rm-confirm-lead">
            <b>No backup is set up for {person.name} yet</b>, so nothing will move — their fixes stay with them. They'll
            be shown as away and won't get the daily reminder.
          </p>
        )}
        {error && <div className="rm-error">{error}</div>}
      </div>
    </Modal>
  )
}

/** The accounts team at a glance — who's in, who's away, who covers, what's on each. */
function TeamPanel({ accountants, items, onAway, onBack }) {
  const canAct = useCanAct()
  return (
    <div className="panel rm-team">
      <div className="panel-head">
        <div>
          <div className="panel-title">Accounts team</div>
          <div className="rp-sec-sub">
            Keka attendance marks people away (no check-in by 11:00 on a working day) and back (checked in). Their
            not-started fixes go to their backup meanwhile. Marking someone away by hand overrides Keka until you mark them back.
          </div>
        </div>
      </div>
      <div className="rm-team-list">
        <div className="rm-team-row rm-team-head">
          <span>Accountant</span>
          <span>Open</span>
          <span>Not started</span>
          <span>Covering</span>
          <span>Backup</span>
          <span>Attendance</span>
          <span />
        </div>
        {accountants.map((a) => {
          const mine = items.filter((i) => !isDone(i) && String(i.assignedAccountantEmail).toLowerCase() === String(a.email).toLowerCase())
          const notStarted = mine.filter((i) => i.status === 'open').length
          const covering = mine.filter((i) => i.originalAccountantEmail).length
          const away = Boolean(a.awaySince)
          return (
            <div key={a.email} className={`rm-team-row${away ? ' away' : ''}`}>
              <span className="rm-team-who">
                <span className="c-name">{a.name}</span>
                <span className={`pill ${away ? 'p-warn' : 'p-ok'}`}>
                  <span className="d" />
                  {away ? 'Away' : 'In'}
                </span>
                {away && (
                  <span className="c-sub">
                    since {whenText(a.awaySince)} · {String(a.awayMarkedBy ?? '').startsWith('keka') ? 'no check-in on Keka' : 'marked by an admin'}
                  </span>
                )}
              </span>
              <span className="rm-team-n">{mine.length}</span>
              <span className="rm-team-n">{notStarted}</span>
              <span className={`rm-team-n${covering ? '' : ' mute'}`}>{covering || '—'}</span>
              <span className={a.backupName ? undefined : 'mute'}>{a.backupName ?? 'No backup set'}</span>
              <span className={a.kekaEmployeeId ? undefined : 'rm-warn'} title={a.kekaEmployeeId ? undefined : 'Not linked to Keka — away/back is manual only'}>
                {a.kekaEmployeeId ? 'From Keka' : 'Manual only'}
              </span>
              <span className="rm-team-act">
                {canAct && (
                  <button type="button" className="btn btn-sm" onClick={() => (away ? onBack(a) : onAway(a, notStarted))}>
                    {away ? 'Mark back' : 'Mark away'}
                  </button>
                )}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function AccountantRemediation() {
  const [items, setItems] = useState(null)
  const [accountants, setAccountants] = useState([])
  const [status, setStatus] = useState('open')
  const [category, setCategory] = useState('all')
  const [who, setWho] = useState('all')
  const [openId, setOpenId] = useState(null)
  const [error, setError] = useState(null)
  const [awayFor, setAwayFor] = useState(null) // { person, back, notStarted }
  const [toast, setToast] = useState(null)

  // One fetch of everything, filtered locally — same approach as the
  // Ledger Collector's own filters, and it's what lets the tiles above count
  // across every status regardless of which tab is showing.
  const load = useCallback(async () => {
    try {
      const [rows, dir] = await Promise.all([api.listRemediationItems({ status: 'all' }), api.listAccountants()])
      setItems(rows.items ?? [])
      setAccountants(dir.accountants ?? [])
      setError(null)
    } catch (e) {
      setError(e.message)
      setItems([])
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const nameByEmail = useMemo(
    () => new Map(accountants.map((a) => [String(a.email).toLowerCase(), a.name])),
    [accountants]
  )
  const accountantName = useCallback(
    (email) => nameByEmail.get(String(email ?? '').toLowerCase()) ?? email ?? '—',
    [nameByEmail]
  )

  const stats = useMemo(() => {
    const list = items ?? []
    const open = list.filter((i) => !isDone(i))
    return {
      open: open.length,
      inProgress: open.filter((i) => i.status === 'in_progress').length,
      paused: open.filter((i) => i.status === 'paused').length,
      notStarted: open.filter((i) => i.status === 'open').length,
      done: list.length - open.length,
      oldestDays: open.length
        ? Math.floor((Date.now() - Math.min(...open.map((i) => new Date(i.createdAt).getTime()))) / 86_400_000)
        : null,
      openValue: open.reduce((sum, i) => sum + (Number(i.amount) || 0), 0),
    }
  }, [items])

  const whoOptions = useMemo(
    () => [
      { value: 'all', label: 'Everyone' },
      ...accountants.map((a) => ({ value: String(a.email).toLowerCase(), label: a.name })),
    ],
    [accountants]
  )

  const visible = useMemo(
    () =>
      (items ?? []).filter((i) => {
        if (status === 'open' && isDone(i)) return false
        if (status === 'confirmed' && !isDone(i)) return false
        if (category !== 'all' && (i.category || '') !== category) return false
        if (who !== 'all' && String(i.assignedAccountantEmail ?? '').toLowerCase() !== who) return false
        return true
      }),
    [items, status, category, who]
  )

  const narrowed = category !== 'all' || who !== 'all'

  return (
    <section className="screen">
      <div className="topbar">
        <div>
          <h1 className="page-title">Ledger Remediation</h1>
          <div className="page-sub">
            Our-side ledger fixes, routed to the accounts team by category — who's started what, what's paused and why,
            and how long each took. Nudged over Cliq until marked done.
          </div>
        </div>
      </div>

      <div className="stats">
        {[
          ['Open', stats.open, 'var(--amber)', stats.open ? `${stats.inProgress} in progress · ${stats.paused} paused · ${stats.notStarted} not started` : null],
          ['Done', stats.done, 'var(--green)'],
          ['Oldest open', stats.oldestDays === null ? '—' : stats.oldestDays === 0 ? 'Today' : `${stats.oldestDays}d`, 'var(--red)'],
          ['Open value', stats.open ? amountText(stats.openValue) : '—', 'var(--blue)'],
        ].map(([label, value, accent, meta]) => (
          <div className="stat" key={label}>
            <div className="stat-accent" style={{ background: accent }} />
            <div className="stat-label">{label}</div>
            <div className="stat-value">{items === null ? '—' : value}</div>
            {items !== null && meta && <div className="stat-meta">{meta}</div>}
          </div>
        ))}
      </div>

      {accountants.length > 0 && items !== null && (
        <TeamPanel
          accountants={accountants}
          items={items}
          onAway={(person, notStarted) => setAwayFor({ person, back: false, notStarted })}
          onBack={(person) => setAwayFor({ person, back: true, notStarted: 0 })}
        />
      )}

      <div className="panel">
        <div className="panel-head rm-head">
          <Segmented options={STATUS_FILTERS} value={status} onChange={setStatus} title="Show by status" />
          <div className="rm-filters">
            <Select options={CATEGORY_FILTERS} value={category} onChange={setCategory} title="Category" />
            <Select options={whoOptions} value={who} onChange={setWho} title="Accountant" />
            {narrowed && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  setCategory('all')
                  setWho('all')
                }}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="panel-pad">
            <div className="note" style={{ marginTop: 0 }}>{error}</div>
          </div>
        )}

        <div className="tbl" style={{ border: 'none', borderRadius: 0 }}>
          <div className="thead" style={{ gridTemplateColumns: ROW_COLS }}>
            <span>Client</span>
            <span>Category</span>
            <span>Issue</span>
            <span style={{ textAlign: 'right' }}>Amount</span>
            <span>Assigned to</span>
            <span>Raised</span>
            <span>Status</span>
          </div>
          {items === null && <SkeletonRow cols={ROW_COLS} cells={7} count={4} />}
          {items !== null && visible.length === 0 && (
            <div className="empty">
              {items.length === 0 ? 'No remediation items yet.' : 'Nothing matches these filters.'}
            </div>
          )}
          {visible.map((item) => (
            <ItemRow key={item._id} item={item} accountantName={accountantName} onOpen={() => setOpenId(item._id)} />
          ))}
        </div>
      </div>

      {openId && <ItemDrawer itemId={openId} accountantName={accountantName} onClose={() => setOpenId(null)} />}

      {awayFor && (
        <AwayModal
          {...awayFor}
          onClose={() => setAwayFor(null)}
          onDone={(person, back, out) => {
            setAwayFor(null)
            setToast(
              back
                ? `${person.name} is back — ${out.returned} returned${out.keptByCover ? `, ${out.keptByCover} kept by the cover` : ''}.`
                : out.backup
                  ? `${person.name} marked away — ${out.moved} moved to ${person.backupName}.`
                  : `${person.name} marked away — no backup set, nothing moved.`
            )
            setTimeout(() => setToast(null), 4000)
            load()
          }}
        />
      )}

      {toast && (
        <div className="toast-wrap">
          <div className="toast toast-ok">{toast}</div>
        </div>
      )}
    </section>
  )
}
